'use client';

import * as React from "react";
import * as LabelPrimitive from "@radix-ui/react-label";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "../../utils/cn";

/**
 * Input — MV5 text field primitive.
 *
 * Types: text | email | password | number | search (accept anything native
 * <input type> supports; the variants below tune padding/icon slots).
 *
 * Sizes:
 *   L — 48px tall, body-16 label. Hero forms.
 *   M — 40px tall, body-14 label. Default.
 *   S — 32px tall, caption-12 label. Toolbars + inline.
 *
 * States: default / hover / focus / disabled / error / loading.
 *
 * RTL: uses logical margin/padding (ps-/pe-) so start/end icons swap under
 * `dir="rtl"`.
 *
 * Composition: `<Input.Root>` is not exposed — instead callers compose the
 * flat primitives `Label`, `Input`, `HelperText` themselves for maximum
 * layout control (matches shadcn form-field composition).
 */

const inputVariants = cva(
  [
    "block w-full font-sans",
    "bg-chrome-surface text-ink-primary placeholder:text-ink-muted",
    "border rounded-card",
    "transition-colors duration-micro ease-standard",
    "outline-none",
    "focus-visible:ring-2 focus-visible:ring-accent-violet focus-visible:ring-offset-2 focus-visible:ring-offset-chrome-surface",
    "disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-chrome-panel",
    // Number spinner cleanup — MV5 uses caret-only edits.
    "[&[type=number]]:[appearance:textfield]",
    "[&[type=number]]:[-moz-appearance:textfield]",
  ].join(" "),
  {
    variants: {
      size: {
        L: "h-[48px] text-body-16 px-space-16",
        M: "h-[40px] text-body-14 px-space-12",
        S: "h-[32px] text-caption-12 px-space-8",
      },
      state: {
        default: "border-chrome-border hover:border-ink-muted",
        error:
          "border-status-error hover:border-status-error focus-visible:ring-status-error",
      },
      hasLeftIcon: { true: "ps-space-32", false: "" },
      hasRightSlot: { true: "pe-space-32", false: "" },
    },
    defaultVariants: {
      size: "M",
      state: "default",
      hasLeftIcon: false,
      hasRightSlot: false,
    },
  },
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  /** Error state — pairs with `HelperText tone="error"` for visible copy. */
  error?: boolean;
  /** Loading state — disables input, shows spinner on the trailing edge. */
  loading?: boolean;
  /** Optional icon rendered at the start (LTR: left, RTL: right). */
  leftIcon?: React.ReactNode;
  /** Optional icon rendered at the end (LTR: right, RTL: left). */
  rightIcon?: React.ReactNode;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  function Input(
    {
      className,
      size,
      error = false,
      loading = false,
      disabled,
      leftIcon,
      rightIcon,
      ...props
    },
    ref,
  ) {
    const state = error ? "error" : "default";
    const trailing = loading ? (
      <Loader2 className="animate-spin size-4" aria-hidden="true" />
    ) : (
      rightIcon
    );
    const hasLeftIcon = Boolean(leftIcon);
    const hasRightSlot = Boolean(trailing);

    if (!hasLeftIcon && !hasRightSlot) {
      // Bare input — no wrapper needed, keeps the DOM light.
      return (
        <input
          ref={ref}
          data-slot="input"
          data-error={error || undefined}
          data-loading={loading || undefined}
          aria-invalid={error || undefined}
          aria-busy={loading || undefined}
          disabled={disabled || loading}
          className={cn(inputVariants({ size, state, className }))}
          {...props}
        />
      );
    }

    return (
      <div className="relative w-full" data-slot="input-root">
        {hasLeftIcon && (
          <span
            className="absolute inset-y-0 start-0 flex items-center ps-space-12 text-ink-muted pointer-events-none"
            aria-hidden="true"
          >
            {leftIcon}
          </span>
        )}
        <input
          ref={ref}
          data-slot="input"
          data-error={error || undefined}
          data-loading={loading || undefined}
          aria-invalid={error || undefined}
          aria-busy={loading || undefined}
          disabled={disabled || loading}
          className={cn(
            inputVariants({
              size,
              state,
              hasLeftIcon,
              hasRightSlot,
              className,
            }),
          )}
          {...props}
        />
        {hasRightSlot && (
          <span
            className="absolute inset-y-0 end-0 flex items-center pe-space-12 text-ink-muted pointer-events-none"
            aria-hidden="true"
          >
            {trailing}
          </span>
        )}
      </div>
    );
  },
);
Input.displayName = "Input";

/**
 * Label — companion label primitive, wraps Radix Label for click-to-focus.
 */
export const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root>
>(function Label({ className, ...props }, ref) {
  return (
    <LabelPrimitive.Root
      ref={ref}
      data-slot="label"
      className={cn(
        "block text-body-14 font-medium text-ink-primary mb-space-4",
        "peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
        className,
      )}
      {...props}
    />
  );
});
Label.displayName = "Label";

/**
 * HelperText — hint or error copy below an input.
 */
export interface HelperTextProps
  extends React.HTMLAttributes<HTMLParagraphElement> {
  tone?: "default" | "error";
}
export const HelperText = React.forwardRef<HTMLParagraphElement, HelperTextProps>(
  function HelperText({ className, tone = "default", ...props }, ref) {
    return (
      <p
        ref={ref}
        data-slot="helper-text"
        data-tone={tone}
        className={cn(
          "mt-space-4 text-caption-12",
          tone === "error" ? "text-status-error" : "text-ink-muted",
          className,
        )}
        {...props}
      />
    );
  },
);
HelperText.displayName = "HelperText";

export { inputVariants };
